import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import React, { useEffect, useId, useState } from "react"
import QuoteForm from "./quote_form"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import Input from "@/common/components/input"
import Label from "@/common/components/label"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const quoteSectionHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [2],
      HTMLAttributes: {
        class: "mb-3 heading-three",
        id: "quote-heading",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteSectionParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-0 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const QuoteSectionFragment = graphql(`
  fragment FrontpagesQuoteSection on FrontpagesQuoteSection {
    id
    headingJSON: heading
    description
    buttonLabel
    inputLabel

    quoteForm {
      ...FrontpagesQuoteForm
    }
  }
`)

const QuoteSectionBlock: React.FC<BlockProps<typeof QuoteSectionFragment>> = ({ blockable }) => {
  const block = getFragment(QuoteSectionFragment, blockable)
  const [formOpen, setFormOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    setEnabled(true)
  }, [])

  const openForm = (e: React.FormEvent) => {
    e.preventDefault()

    setFormOpen(true)
  }

  const id = useId()

  return (
    <div className="container">
      <div className="flex flex-col gap-6 rounded-2xl bg-blue-200 px-4 py-10 lg:flex-row lg:gap-12 lg:px-8">
        <div className="flex flex-1 flex-col justify-center">
          <StaticTiptapDisplay extensions={quoteSectionHeadingStack} json={block.headingJSON} />
          <StaticTiptapDisplay extensions={quoteSectionParagraphStack} json={block.description} />
        </div>
        <div className="flex flex-1 flex-col justify-center">
          <form onSubmit={openForm}>
            <Label id={id}>{`*${block.inputLabel}`}</Label>
            <div className="flex flex-col gap-4 lg:flex-row lg:gap-2">
              <Input
                id={id}
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={!enabled}
                className="!mb-0 flex-1"
              />
              <button
                className="button button-primary mb-0 w-full lg:w-auto"
                type="submit"
                disabled={!enabled}
                data-gtm="fp-quote-cta-button"
              >
                {block.buttonLabel}
              </button>
            </div>
          </form>
        </div>
      </div>

      <QuoteForm form={block.quoteForm} open={formOpen} setOpen={setFormOpen} email={email} />
    </div>
  )
}

export default QuoteSectionBlock
